'use strict';
/*
  neon — synthwave / outrun. A full composited scene rendered to an offscreen
  buffer, then bloomed: gradient sky, scanline sun, audio-reactive skyline,
  parallax starfield, scrolling perspective grid, and glowing kinetic type.
  Captions are drawn into the scene so the bloom pass makes them glow.
*/
const { layoutCue } = require('./_common');
const { roundRect, FONTS } = require('../lib/draw');

const palette = { bg: '#05030f', fg: '#eafbff', accent: '#ff2bd6', accent2: '#21e6ff', sun: '#ffd24a' };
const HOR = 0.56; // horizon as fraction of H

function init(st){
  st.s.font = st.tk.registerFamily('ForgeFutura', FONTS.futura) || undefined;
  st.s.sc = st.tk.createCanvas(st.W, st.H);
  st.s.scx = st.s.sc.getContext('2d');
  st.s.gridT = 0; st.s.glow = 0.4; st.s.sun = 0; st.s.flick = 1;
  const stars = []; const n = 110;
  for (let i = 0; i < n; i++) stars.push({ x: st.rnd() * st.W, y: st.rnd() * st.H * HOR, z: 0.3 + st.rnd() * 0.7, tw: st.rnd() * 6.28 });
  st.s.stars = stars;
}
function onBeat(st, power){ st.s.glow = Math.min(1.4, st.s.glow + power * 0.55); st.s.sun = Math.min(1, st.s.sun + power * 0.4); }

function scene(st){
  const { W, H } = st, g = st.s.scx, horizon = H * HOR;
  const over = st.overlayMode;
  st.s.glow *= 0.9; st.s.sun *= 0.9; st.s.gridT += 0.012 + (st.b / 255) * 0.03;

  if (over) { g.clearRect(0, 0, W, H); }
  else {
    const sky = g.createLinearGradient(0, 0, 0, horizon);
    sky.addColorStop(0, '#0a0524'); sky.addColorStop(0.55, '#2a0a44'); sky.addColorStop(1, '#7d1a5e');
    g.fillStyle = sky; g.fillRect(0, 0, W, horizon);
    const grd = g.createLinearGradient(0, horizon, 0, H);
    grd.addColorStop(0, '#1a0530'); grd.addColorStop(1, '#05030f');
    g.fillStyle = grd; g.fillRect(0, horizon, W, H - horizon);
  }

  // ---- stars (above horizon) ----
  if (!over) for (const s of st.s.stars) {
    s.y += s.z * 0.25; if (s.y > horizon) { s.y = 0; s.x = st.rnd() * W; }
    const tw = 0.5 + 0.5 * Math.sin(st.NOW / 380 + s.tw);
    g.globalAlpha = (0.25 + tw * 0.6) * s.z; g.fillStyle = '#cfe9ff';
    const r = s.z * 1.8; g.fillRect(s.x, s.y, r, r);
  }
  g.globalAlpha = 1;

  // ---- sun with horizontal scanline cuts ----
  if (!over) {
    const pr = 1 + st.s.sun * 0.05, R = W * 0.27 * pr, cy = horizon;
    g.save();
    g.beginPath(); g.rect(0, 0, W, horizon + 4); g.clip(); // only the top half shows
    const sun = g.createLinearGradient(0, cy - R, 0, cy + R);
    sun.addColorStop(0, '#fff1a8'); sun.addColorStop(0.45, palette.sun); sun.addColorStop(0.75, '#ff5ea0'); sun.addColorStop(1, '#ff2bd6');
    g.fillStyle = sun; g.beginPath(); g.arc(st.CX, cy, R, 0, 7); g.fill();
    // cuts: erase increasingly tall bars toward the bottom
    g.globalCompositeOperation = 'destination-out';
    for (let i = 0; i < 16; i++) { const y = cy - R * 0.15 + i * (R * 1.1 / 16); const h = (i / 16) * 9 + 2; g.fillStyle = '#000'; g.fillRect(st.CX - R, y, R * 2, h); }
    g.restore();
  }

  // ---- audio-reactive skyline sitting on the horizon ----
  if (st.spectrum && !over) {
    const { NB, data } = st.spectrum, f = st.f, bw = W / NB;
    for (let i = 0; i < NB; i++) {
      const v = (data[f * NB + i] || 0) / 255;
      const bh = (0.02 + v * 0.16) * H, x = i * bw;
      g.fillStyle = '#080414'; g.fillRect(x, horizon - bh, bw * 0.9, bh);            // building silhouette
      const hue = 188 + (i / NB) * 132;                                              // cyan -> magenta
      g.fillStyle = `hsl(${hue},100%,${55 + v * 15}%)`;                              // glowing rooftop
      g.fillRect(x, horizon - bh, bw * 0.9, Math.max(3, H * 0.005));
    }
  }

  // ---- perspective grid floor (music only — talk keeps focus on the captions) ----
  if (st.reactive) {
    const ga = 0.22 + st.s.glow * 0.18, gh = 190 + Math.sin(st.NOW / 1500) * 55 + st.energy * 30;
    g.strokeStyle = `hsla(${gh.toFixed(0)},100%,62%,${ga.toFixed(3)})`; g.lineWidth = over ? 1.5 : 2.5;
    for (let i = -11; i <= 11; i++) { const xb = st.CX + i * (W / 7); g.beginPath(); g.moveTo(st.CX, horizon); g.lineTo(xb, H); g.stroke(); }
    const rows = 16;
    for (let i = 0; i < rows; i++) {
      const fr = ((i + (st.s.gridT % 1)) / rows), y = horizon + Math.pow(fr, 2.3) * (H - horizon);
      g.globalAlpha = Math.min(1, fr * 1.6) * (over ? 0.5 : 1);
      g.beginPath(); g.moveTo(0, y); g.lineTo(W, y); g.stroke();
    }
    g.globalAlpha = 1;
  }

  if (over) { // readability scrim behind captions when over footage
    const sc = g.createLinearGradient(0, H * 0.5, 0, H);
    sc.addColorStop(0, 'rgba(5,3,15,0)'); sc.addColorStop(1, 'rgba(5,3,15,0.55)');
    g.fillStyle = sc; g.fillRect(0, H * 0.5, W, H * 0.5);
  }

  // ---- captions (into the scene so they bloom) ----
  st.s.flick = st.rnd() > 0.06 ? (0.7 + 0.3 * Math.sin(st.NOW / 45)) : 0.35;
  layoutCue(g, st, { sizes: { big: 142, mid: 116, small: 66 }, anchor: 0.72, safeW: 0.86, reveal: 0.26, stagger: 0.05, perLetter: true, letterStagger: 0.024, trans: 0.16, family: st.s.font, paintToken });
}

function paintToken(ctx, o){
  const { T, cx, cy, px, scl, alpha, emph, st } = o, tk = st.tk;
  ctx.save(); ctx.globalAlpha = alpha; ctx.translate(cx, cy); ctx.scale(scl, scl);
  ctx.font = tk.font(px, 900, st.s.font); ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  const w = ctx.measureText(T).width;
  if (emph) {
    const fl = st.s.flick;
    ctx.lineWidth = Math.max(3, px * 0.05);
    ctx.shadowColor = palette.accent; ctx.shadowBlur = 26 * fl;
    ctx.strokeStyle = palette.accent;
    tk.chip(ctx, T, px, 0.20, 0.18); ctx.stroke();
    ctx.shadowBlur = 16 * fl; ctx.fillStyle = '#ffffff'; ctx.fillText(T, 0, 0);
    ctx.globalAlpha = alpha * 0.55; ctx.fillStyle = palette.accent; ctx.fillText(T, 0, 0);
  } else {
    ctx.globalCompositeOperation = 'screen';
    ctx.fillStyle = 'rgba(255,43,214,0.55)'; ctx.fillText(T, -3, 0);
    ctx.fillStyle = 'rgba(33,230,255,0.55)'; ctx.fillText(T, 3, 0);
    ctx.globalCompositeOperation = 'source-over';
    ctx.shadowColor = palette.accent2; ctx.shadowBlur = 18; ctx.fillStyle = '#eafbff'; ctx.fillText(T, 0, 0);
  }
  ctx.restore();
}

// all visuals are produced in background(); drawCue is intentionally omitted.
function background(ctx, st){ scene(st); }

function overlay(ctx, st){
  const tk = st.tk, W = st.W, H = st.H;
  const pz = 1 + st.punch * 1.2; // beat zoom-punch on the whole scene
  ctx.save(); ctx.translate(W / 2, H / 2); ctx.scale(pz, pz); ctx.translate(-W / 2, -H / 2);
  ctx.drawImage(st.s.sc, 0, 0);            // composite the scene
  tk.bloom(ctx, st.s.sc, 0.8 + st.s.glow * 0.25, 2, 8); // glow
  ctx.restore();
  ctx.save(); ctx.globalAlpha = 0.09; ctx.drawImage(tk.scanlines(0.5, 4), 0, 0); ctx.restore();
  ctx.save(); ctx.globalAlpha = 0.045; ctx.globalCompositeOperation = 'screen'; ctx.drawImage(tk.grain(), -Math.random() * 100, -Math.random() * 100); ctx.restore();
  ctx.drawImage(tk.vignette(0.72, 320), 0, 0);
  if (!st.overlayMode) {
    const pg = ctx.createLinearGradient(0, 0, st.W, 0);
    pg.addColorStop(0, palette.accent2); pg.addColorStop(1, palette.accent);
    ctx.fillStyle = pg; ctx.fillRect(0, st.H - 7, st.W * st.progress, 7);
  }
}

module.exports = { id: 'neon', label: 'Neon · synthwave glow', palette, init, background, overlay, onBeat, paintToken };
